import { useState, useEffect } from "react";
import { db } from "@/firebase";
import { collection, getDocs } from "firebase/firestore";
import { coursesList } from "@/data/global_config";

const filterEnabled = (items) =>
  (items || []).filter((item) => item.isEnabled !== false);

// Fields that are NOT section arrays
const NON_SECTION_FIELDS = new Set([
  "name", "moduleId", "isEnabled", "order", "data",
  "status", "startDate", "endDate", "createdAt",
]);

// Known sections in preferred display order (assessments always last)
const SECTION_ORDER = ["preReading", "videos"];
const ALWAYS_LAST   = ["assessments"];

const buildModule = (docId, raw, milestoneId = "") => {
  const milestonePrefix = milestoneId
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
  const docIdClean = docId.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  const moduleId = raw.moduleId || (milestonePrefix ? `${milestonePrefix}-${docIdClean}` : docIdClean);
  const subtopics = [];

  const getField = (key) =>
    raw[key] || raw[key.trim()] || raw[key + " "] || raw[key.charAt(0).toUpperCase() + key.slice(1)] || [];

  const slugify = (str) =>
    str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  // Items with an explicit `order` get pinned to that position (1-indexed).
  // Items without `order` keep their original relative sequence and fill the gaps.
  const applyOrder = (items) => {
    const withOrder = [];
    const withoutOrder = [];

    items.forEach((item) => {
      if (item.order !== undefined && item.order !== null) {
        withOrder.push(item);
      } else {
        withoutOrder.push(item);
      }
    });

    // Nothing has an order field -> keep default array order, unchanged
    if (withOrder.length === 0) return items;

    withOrder.sort((a, b) => a.order - b.order);

    const result = [];
    let oi = 0, ui = 0;
    const total = items.length;

    for (let pos = 1; pos <= total; pos++) {
      if (oi < withOrder.length && withOrder[oi].order === pos) {
        result.push(withOrder[oi]);
        oi++;
      } else if (ui < withoutOrder.length) {
        result.push(withoutOrder[ui]);
        ui++;
      }
    }

    // Safety net: push any leftovers (e.g. order values beyond array length, or duplicates)
    while (oi < withOrder.length) result.push(withOrder[oi++]);
    while (ui < withoutOrder.length) result.push(withoutOrder[ui++]);

    return result;
  };

  const addSection = (key, items) => {
  const filtered = applyOrder(filterEnabled(items));
  if (filtered.length === 0) return;
  const slug = slugify(key);

  if (key === "preReading") {
    filtered.forEach((item, i) => {
      subtopics.push({
        id:    `${moduleId}-${slug}-external-link-${String(i + 1).padStart(2, "0")}`,
        title: item.title,
        url:   item.url,
      });
    });
  } else {
    const label = key.charAt(0).toUpperCase() + key.slice(1);
    subtopics.push({
      id:    `${moduleId}-${slug}`,
      title: label,
      links: filtered.map((item, i) => ({
        id:    item.isExternal
          ? `${moduleId}-${slug}-external-link-${String(i + 1).padStart(2, "0")}`
          : `${moduleId}-${slug}-inline-link-${String(i + 1).padStart(2, "0")}`,
        title: item.title,
        url:   item.url,
      })),
    });
  }
};

  // 1. Render known priority sections first (preReading, videos)
  SECTION_ORDER.forEach((key) => {
    const items = getField(key);
    if (items.length > 0) addSection(key, items);
  });

  // 2. Render all other array fields dynamically sorted alphabetically
  //    (classwork-01, classwork-02, reference, tools etc.) — anything not in skip list
  const skipKeys = new Set([...NON_SECTION_FIELDS, ...SECTION_ORDER, ...ALWAYS_LAST]);
  const dynamicKeys = Object.keys(raw)
    .map(k => k.trim())
    .filter(k => !skipKeys.has(k) && Array.isArray(raw[k]) && raw[k].length > 0)
    .sort(); // alphabetical: classwork-01, classwork-02, reference, videos...

  dynamicKeys.forEach((key) => addSection(key, raw[key]));

  // 3. Render assessments last
  ALWAYS_LAST.forEach((key) => {
    const items = getField(key);
    if (items.length > 0) addSection(key, items);
  });

  return { id: moduleId, title: raw.name || docId, subtopics };
};

const fetchFromFirestore = async (orgCode) => {
  const flatList = [];

 const milestonesSnap = await getDocs(
  collection(db, "CorporateClients", orgCode, "programInfo")
);

// Sort courses by createdAt
const sortedMilestones = milestonesSnap.docs.sort((a, b) => {
  const aTime = new Date(a.data().createdAt || 0).getTime();
  const bTime = new Date(b.data().createdAt || 0).getTime();

  return aTime - bTime;      // Oldest first
  // return bTime - aTime;   // Newest first
});

for (const milestoneDoc of sortedMilestones) {
    // Skip milestone if explicitly disabled
    if (milestoneDoc.data().isEnabled === false) continue;

    const modulesSnap = await getDocs(
      collection(db, "CorporateClients", orgCode, "programInfo", milestoneDoc.id, "modules")
    );

    const enabledModules = [];
    modulesSnap.forEach((moduleDoc) => {
      const raw = moduleDoc.data();
      if (raw.isEnabled !== false) {
        if (raw.data) {
          try { enabledModules.push(JSON.parse(raw.data)); }
          catch (e) { console.error("Failed to parse:", moduleDoc.id, e); }
        } else {
          enabledModules.push(buildModule(moduleDoc.id, raw, milestoneDoc.id));
        }
      }
    });

    if (enabledModules.length > 0) {
      flatList.push({ class: "seperator", id: milestoneDoc.id, title: milestoneDoc.id });
      flatList.push(...enabledModules);
    }
  }

  return flatList;
};

export const useProgramData = () => {
  const [topics, setTopics]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const courseCode   = localStorage.getItem("courseCode");
    const orgCode      = localStorage.getItem("orgCode");
    const courseConfig = coursesList[courseCode] || {};

    console.log("overrideFirst:", courseConfig.moduleOverrideFirst?.length);
    console.log("data:", courseConfig.data?.length);
    console.log("overrideLast:", courseConfig.moduleOverrideLast?.length);

    const {
      data,
      moduleOverrideFirst = [],
      moduleOverrideLast  = [],
    } = courseConfig;

    const resolve = async () => {
      try {
        let middle = [];

        // Scenario 1 & 2 — data field present → use local JSON
        if (data) {
          middle = data;

        // Scenario 3 & 4 — no data field → fetch from Firestore
        } else {
          if (!orgCode) {
            setError("orgCode not found in localStorage");
            setLoading(false);
            return;
          }
          middle = await fetchFromFirestore(orgCode);
        }

        // Final: overrideFirst + middle + overrideLast
        console.log("Final topics count:", moduleOverrideFirst.length, "+", middle.length, "+", moduleOverrideLast.length);
        setTopics([...moduleOverrideFirst, ...middle, ...moduleOverrideLast]);
      } catch (err) {
        console.error("useProgramData error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    resolve();
  }, []);

  return { topics, loading, error };
};